/**
 * WAB — the compact binary waveform the desktop app writes next to the waveform
 * PNG (ROVR-3809, `WaveFormImageExtractor::save`). It is the replacement for the
 * bitmap: ~3 peaks per second at any length, instead of the PNG's fixed number of
 * columns (912 for a 1000px image, whether the audio is 3 minutes or 2 hours).
 *
 * Layout — Qt `QDataStream`, big-endian, single-precision floats:
 *
 * | offset | type              | value                                  |
 * | ------ | ----------------- | -------------------------------------- |
 * | 0      | uint32            | magic `0x57414200` ("WAB\0")           |
 * | 4      | uint32            | version (2 = one channel, 3 = stereo)  |
 * | 8      | uint64            | duration in milliseconds               |
 * | 16     | uint64            | `count` = peaks per channel            |
 * | 24     | float32 × count   | channel 0, then channel 1 (v3+)        |
 *
 * Each value is `clamp(max(|bucket_min|, |bucket_max|), 0, 1)` — an absolute
 * peak, so it is drawn mirrored around the centre line.
 *
 * Reference implementation: `desktop-app/tools/wab_js_renderer/app.js`.
 */

export const WAB_MAGIC = 0x57414200;
export const WAB_HEADER_BYTES = 4 + 4 + 8 + 8;
/** Version 2 stores a single mixed channel; 3 and above store both channels. */
export const WAB_MIN_VERSION = 2;

export interface WabData {
  version: number;
  durationSeconds: number;
  /**
   * One `Float32Array` per channel, always two entries — a version 2 file
   * repeats its single mixed channel so callers never branch on version.
   */
  channels: [Float32Array, Float32Array];
}

export class WabParseError extends Error {}

/** Reads a `.wab` payload. Throws `WabParseError` on anything unexpected. */
export const parseWab = (buffer: ArrayBuffer): WabData => {
  if (buffer.byteLength < WAB_HEADER_BYTES) {
    throw new WabParseError(
      `WAB too small: ${buffer.byteLength} bytes, expected at least ${WAB_HEADER_BYTES}`
    );
  }

  const view = new DataView(buffer);
  const magic = view.getUint32(0);
  if (magic !== WAB_MAGIC) {
    throw new WabParseError(`Bad WAB magic 0x${magic.toString(16)}`);
  }

  const version = view.getUint32(4);
  if (version < WAB_MIN_VERSION) {
    throw new WabParseError(`Unsupported WAB version ${version}`);
  }

  const durationMs = Number(view.getBigUint64(8));
  const count = Number(view.getBigUint64(16));
  const channelCount = version === 2 ? 1 : 2;
  const expectedBytes = WAB_HEADER_BYTES + count * channelCount * 4;

  if (!Number.isFinite(count) || count <= 0) {
    throw new WabParseError(`Bad WAB peak count ${count}`);
  }
  if (buffer.byteLength < expectedBytes) {
    throw new WabParseError(
      `Truncated WAB: ${buffer.byteLength} bytes, expected ${expectedBytes}`
    );
  }

  const readChannel = (index: number): Float32Array => {
    const channel = new Float32Array(count);
    const offset = WAB_HEADER_BYTES + index * count * 4;
    for (let i = 0; i < count; i += 1) {
      channel[i] = view.getFloat32(offset + i * 4);
    }
    return channel;
  };

  const left = readChannel(0);
  const right = channelCount === 2 ? readChannel(1) : left;

  return {
    version,
    durationSeconds: durationMs / 1000,
    channels: [left, right],
  };
};

/**
 * Highest absolute peak across both channels between two positions expressed as
 * fractions of the whole track — the "monomix" sampling of the reference
 * renderer. Scanning (rather than picking the middle sample) is what keeps
 * transients visible when a pixel spans many peaks at low zoom.
 */
export const sampleWabMonomix = (
  data: WabData,
  startRatio: number,
  endRatio: number
): number => {
  const [left, right] = data.channels;
  const total = left.length;
  if (!total) return 0;

  const startIndex = Math.max(0, Math.floor(startRatio * total));
  const endIndex = Math.min(
    total,
    Math.max(Math.floor(endRatio * total), startIndex + 1)
  );

  let peak = 0;
  for (let i = startIndex; i < endIndex; i += 1) {
    const value = left[i] > right[i] ? left[i] : right[i];
    if (value > peak) peak = value;
  }
  return peak;
};

/**
 * The waveform buckets send no CORS headers, so reading one straight from the
 * browser is refused. In local development the request goes through the dev
 * server instead, which is not subject to CORS (see `src/setupProxy.js`), so
 * the renderer can be worked on and reviewed before the bucket rule lands.
 * Deployed builds always fetch the url directly.
 */
const wabRequestUrl = (url: string): string =>
  process.env.NODE_ENV === "development"
    ? `/__waveform-binary?url=${encodeURIComponent(url)}`
    : url;

/** Fetches and parses a `.wab`. Rejects on network, CORS or format errors. */
export const fetchWab = async (
  url: string,
  signal?: AbortSignal
): Promise<WabData> => {
  const response = await fetch(wabRequestUrl(url), { signal });
  if (!response.ok) {
    throw new WabParseError(
      `Failed to download WAB: ${response.status} ${response.statusText}`
    );
  }
  return parseWab(await response.arrayBuffer());
};
